import { useEffect, useState } from 'react'
import { Background, ConnectionLineType, MarkerType, MiniMap, ReactFlow, useEdgesState, useNodesState } from 'reactflow'

import type { ReactElement } from 'react'
import type { ITodolistTemplateTask } from '~/types/todolist-template'
import type { IUserPublic } from '~/types/user'

import TodoDetailsHeader from './Header'

import SetTodolistModal from '../SetTodolistModal'

import BackNavigation from '~/components/BackNavigation'
import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import Resizer from '~/components/ReactFlow/Resizer'
import ViewTitle from '~/components/ViewTitle'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useRequiredParams } from '~/hooks/useRequiredParams'
import { useTodolistsDetailsQuery } from '~/queries/todolists'
import { useGetMeAsUserQuery, useUserDetailsQuery } from '~/queries/users'
import { edgeTypes, getLayoutedElements, nodeTypes } from '~/utils/react-flow'

import './styles.scss'

const TodolistDetails = (): ReactElement => {
  /** Local state */

  const { todolistId } = useRequiredParams<{ todolistId: string }>()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const [nodes, setNodes] = useNodesState<ITodolistTemplateTask>([])

  const [edges, setEdges] = useEdgesState<ITodolistTemplateTask>([])

  /** Queries & Mutations */

  const { data: user } = useGetMeAsUserQuery()

  const { data: todolistFlow, isLoading: todolistLoading } = useTodolistsDetailsQuery(todolistId)

  const formatCreateAtUser = ({ name, picture, profile }: IUserPublic) => ({
    date: todolistFlow?.todolist.created_at || 0,
    job: profile?.job_title || '',
    name,
    picture,
  })

  const formatUpdateAtUser = ({ name, picture, profile }: IUserPublic) => ({
    date: todolistFlow?.todolist.updated_at || 0,
    job: profile?.job_title || '',
    name,
    picture,
  })

  const { data: createdByUser } = useUserDetailsQuery(
    todolistFlow?.todolist.created_by,
    !!todolistFlow,
    formatCreateAtUser
  )

  const { data: updatedByUser } = useUserDetailsQuery(
    todolistFlow?.todolist.updated_by,
    !!todolistFlow,
    formatUpdateAtUser
  )

  /** UseEffects */

  useDocumentTitle('Todolist Details')

  /**
   * Initialize nodes and edges when data is fetched
   */
  useEffect(() => {
    if (todolistFlow?.tasks) {
      const initialNodes = []
      const initialEdges = []

      for (const task of todolistFlow.tasks) {
        initialNodes.push({
          data: { ...task, myId: user?.user_id },
          id: task.template_task_id,
          position: { x: 0, y: 0 },
          type: 'todolist-task-card',
        })

        if (task.previous_tasks && task.previous_tasks.length) {
          for (const previousStepId of task.previous_tasks) {
            initialEdges.push({
              id: `${previousStepId}-${task.template_task_id}`,
              markerEnd: {
                type: MarkerType.ArrowClosed,
              },
              source: previousStepId,
              target: task.template_task_id,
              type: 'smoothstep',
            })
          }
        }
      }

      const { edges: layoutedEdges, nodes: layoutedNodes } = getLayoutedElements(initialNodes, initialEdges, {
        direction: 'TB',
        nodeHeight: 150,
        nodeWidth: 325,
      })

      setNodes(layoutedNodes)
      setEdges(layoutedEdges)
    }
  }, [setNodes, setEdges, todolistFlow?.tasks, user?.user_id])

  /** Handlers */

  const handleModalState = (isOpen: boolean) => () => setIsModalOpen(isOpen)

  /** Render */

  if (todolistLoading) {
    return (
      <div className="todolist-details-view loader-container">
        <MwMLoader category="item" />
      </div>
    )
  }

  return (
    <Layout className="todolist-details-view">
      <ViewTitle>
        <BackNavigation title="To-do lists" to="/todolists" />
      </ViewTitle>
      {todolistFlow ? (
        <TodoDetailsHeader
          createdBy={createdByUser}
          onEditTodo={handleModalState(true)}
          todolistFlow={todolistFlow}
          updatedBy={updatedByUser}
        />
      ) : null}
      <ContentLayout className="todolist-details-view__content-layout">
        <ReactFlow
          connectionLineType={ConnectionLineType.SmoothStep}
          edgeTypes={edgeTypes}
          edges={edges}
          fitView
          maxZoom={1}
          nodeTypes={nodeTypes}
          nodes={nodes}
          preventScrolling={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background />
          <MiniMap />
          <Resizer />
        </ReactFlow>
      </ContentLayout>
      {todolistFlow ? (
        <SetTodolistModal
          appKey={todolistFlow.todolist.app_key}
          deviceType={todolistFlow.todolist.device_type}
          isOpen={isModalOpen}
          onClose={handleModalState(false)}
          onFailure={handleModalState(false)}
          onSuccess={handleModalState(false)}
          todolistFlow={todolistFlow}
        />
      ) : null}
    </Layout>
  )
}

export default TodolistDetails
